import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Box,
    Button,
    TextField,
    Tabs,
    Tab,
    IconButton,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ImageUploadField from "../../common/ImageUploadField";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import editorConfiguration from "../../../util/EditorConfiguration";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";

// Create a custom editor configuration for merchandise with cineclub upload
const merchandiseEditorConfig = {
    ...editorConfiguration,
    site: 'cineclub',
    folder: 'MERCHANDISE',
};

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(3),
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    backButton: {
        marginRight: theme.spacing(2),
    },
    formCard: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonGroup: {
        display: "flex",
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
    },
    tabpanel: {
        background: "#FFFFFF",
    },
    box: {
        padding: 0,
    },
    innerBox: {
        background: "#ffffff",
        padding: theme.spacing(3),
        marginBottom: theme.spacing(4),
    },
    sectionText: {
        fontSize: "13px",
        fontWeight: 700,
        paddingLeft: "4px",
        marginBottom: theme.spacing(1),
    },
    input: {
        width: "100%",
        "& button": {
            fontSize: "13px",
            lineHeight: "15px",
        },
    },
    optionCard: {
        marginBottom: theme.spacing(2),
    },
}));

function TabPanel(props) {
    const classes = useStyles();
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`nav-tabpanel-${index}`}
            aria-labelledby={`nav-tab-${index}`}
            {...other}
        >
            <Box p={3} className={classes.box}>
                {children}
            </Box>
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `nav-tab-${index}`,
        "aria-controls": `nav-tabpanel-${index}`,
    };
}

const MerchandiseEditingPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const { id } = useParams();
    const { setLoading } = useLoading();
    
    const isEditMode = !!id && id !== "create";
    const isCreateMode = !id || id === "create";
    const [activeTab, setActiveTab] = useState(0);
    
    const [formData, setFormData] = useState({
        titleEn: "",
        titleTc: "",
        subtitleEn: "",
        subtitleTc: "",
        introductionEn: "",
        introductionTc: "",
        pointPrice: "",
        orderLimit: "",
        status: 1, // 1 = Active, 0 = Inactive
        sequence: "",
    });

    const [merchandiseData, setMerchandiseData] = useState(null);
    const [options, setOptions] = useState([]);
    const [optionDisplayOrder, setOptionDisplayOrder] = useState([]);
    const [errors, setErrors] = useState({});

    // Load existing merchandise data if in edit mode
    useEffect(() => {
        if (isEditMode) {
            loadMerchandiseData();
        }
    }, [id]);

    const loadMerchandiseData = async () => {
        setLoading(true);
        try {
            const response = await apiHelper.cineclub.getMerchandiseById(id);
            if (response.data) {
                const merchandise = response.data;
                // Store the full merchandise object
                setMerchandiseData(merchandise);
                
                // Extract localization data for form fields
                const localization = merchandise.localization || {};
                const en = localization.en || {};
                const tc = localization.tc || {};

                // Convert status: true -> 1 (Active), false -> 0 (Inactive)
                let statusValue = 1;
                if (merchandise.status === false || merchandise.status === 0 || merchandise.status === "INACTIVE") {
                    statusValue = 0;
                }

                setFormData({
                    titleEn: en.title || "",
                    titleTc: tc.title || "",
                    subtitleEn: en.subtitle || "",
                    subtitleTc: tc.subtitle || "",
                    introductionEn: en.introduction || "",
                    introductionTc: tc.introduction || "",
                    pointPrice: merchandise.pointPrice || "",
                    orderLimit: merchandise.orderLimit != null ? String(merchandise.orderLimit) : "",
                    status: statusValue,
                    sequence: merchandise.sequence || "",
                });
                // Load options
                const opts = merchandise.options || [];
                setOptions(opts);
                const sortedIndices = opts.map((_, i) => i).sort((a, b) => (opts[a].sequence ?? 0) - (opts[b].sequence ?? 0));
                setOptionDisplayOrder(sortedIndices);
            }
        } catch (error) {
            console.error("Error loading merchandise data:", error);
            alert("Failed to load merchandise data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleEditorChange = (field) => (event, editor) => {
        const data = editor.getData();
        setFormData(prev => ({
            ...prev,
            [field]: data
        }));
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleStatusChange = (event, newValue) => {
        if (newValue !== null) {
            setFormData(prev => ({
                ...prev,
                status: newValue
            }));
        }
    };

    const handleOptionChange = (index, field) => (event) => {
        const value = event.target.value;
        const updatedOptions = [...options];
        updatedOptions[index] = {
            ...updatedOptions[index],
            [field]: value
        };
        setOptions(updatedOptions);
    };

    const handleOptionStatusChange = (index, newValue) => {
        if (newValue !== null) {
            const updatedOptions = [...options];
            updatedOptions[index] = {
                ...updatedOptions[index],
                status: newValue === 1
            };
            setOptions(updatedOptions);
        }
    };

    const handleOptionImageUpload = async (index, file) => {
        if (!file) return;

        try {
            setLoading(true);
            const result = await apiHelper.cineclub.uploadMembershipFile(file, "MERCHANDISE");
            const url = result.data.url;

            const updatedOptions = [...options];
            updatedOptions[index] = {
                ...updatedOptions[index],
                imagePath: url
            };
            setOptions(updatedOptions);
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Failed to upload image. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleOptionImageDelete = (index) => {
        const updatedOptions = [...options];
        updatedOptions[index] = {
            ...updatedOptions[index],
            imagePath: ""
        };
        setOptions(updatedOptions);
    };

    const handleAddOption = () => {
        const newOption = {
            optionEn: "",
            optionTc: "",
            price: "",
            stock: "",
            imagePath: "",
            status: true,
            sequence: 0,
        };
        setOptionDisplayOrder(prev => [...prev, options.length]);
        setOptions([...options, newOption]);
    };

    const handleDeleteOption = (index) => {
        if (window.confirm("Are you sure you want to delete this option?")) {
            setOptions(options.filter((_, i) => i !== index));
            setOptionDisplayOrder(prev =>
                prev.filter(i => i !== index).map(i => i > index ? i - 1 : i)
            );
        }
    };

    const handleMoveOption = (displayIdx, direction) => {
        const targetIdx = direction === 'up' ? displayIdx - 1 : displayIdx + 1;
        if (targetIdx < 0 || targetIdx >= optionDisplayOrder.length) return;
        const newOrder = [...optionDisplayOrder];
        [newOrder[displayIdx], newOrder[targetIdx]] = [newOrder[targetIdx], newOrder[displayIdx]];
        setOptionDisplayOrder(newOrder);
    };

    const handleSubmit = async () => {
        if (isEditMode && !merchandiseData) {
            alert("Merchandise data not loaded. Please refresh the page.");
            return;
        }

        // Validate required fields for create mode
        if (isCreateMode) {
            if (!formData.titleEn.trim() || !formData.titleTc.trim()) {
                alert("Please fill in both English and Traditional Chinese titles.");
                return;
            }
        }

        // Validate that at least one option is required
        if (!options || options.length === 0) {
            alert("Please add at least one option before submitting.");
            return;
        }

        // Validate orderLimit
        const newErrors = {};
        if (formData.orderLimit && (isNaN(formData.orderLimit) || parseInt(formData.orderLimit) < 0 || !Number.isInteger(parseFloat(formData.orderLimit)))) {
            newErrors.orderLimit = "Order limit must be a non-negative integer or empty";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        try {
            // Build the complete payload with all existing fields and updated localization
            const payload = {
                pointPrice: formData.pointPrice ? parseInt(formData.pointPrice) : (isEditMode ? merchandiseData.pointPrice : 0),
                orderLimit: formData.orderLimit && formData.orderLimit.toString().trim() ? parseInt(formData.orderLimit) : null,
                status: formData.status === 1 ? true : false,
                sequence: formData.sequence ? parseInt(formData.sequence) : (isEditMode ? merchandiseData.sequence : 0),
                localization: {
                    en: {
                        title: formData.titleEn.trim(),
                        subtitle: formData.subtitleEn.trim(),
                        introduction: formData.introductionEn,
                    },
                    tc: {
                        title: formData.titleTc.trim(),
                        subtitle: formData.subtitleTc.trim(),
                        introduction: formData.introductionTc,
                    },
                },
                options: optionDisplayOrder.map((originalIdx, displayIdx) => {
                    const option = options[originalIdx];
                    return {
                        ...option,
                        optionId: option.optionId || null,
                        optionEn: option.optionEn || "",
                        optionTc: option.optionTc || "",
                        orgPrice: option.orgPrice || option.price || 0,
                        price: option.price ? parseFloat(option.price) : 0,
                        stock: option.stock ? parseInt(option.stock) : 0,
                        outOfStock: option.outOfStock || false,
                        imagePath: option.imagePath || "",
                        status: option.status !== undefined ? option.status : true,
                        sequence: displayIdx,
                    };
                }),
            };

            let response;
            if (isEditMode) {
                response = await apiHelper.cineclub.updateMerchandise(id, payload);
            } else {
                response = await apiHelper.cineclub.createMerchandise(payload);
            }
            
            if (response.data && response.data.code === 0) {
                // Success - redirect back to listing page
                history.push("/cineclub/eshop/merchandise");
            } else {
                const action = isEditMode ? "update" : "create";
                console.error(`Failed to ${action} merchandise:`, response);
                alert(`Failed to ${action} merchandise. Please try again.`);
            }
        } catch (error) {
            const action = isEditMode ? "updating" : "creating";
            console.error(`Error ${action} merchandise:`, error);
            alert(`An error occurred while ${action} the merchandise. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        history.push("/cineclub/eshop/merchandise");
    };

    return (
        <div className={classes.content}>
            <Box className={classes.header}>
                <Button
                    className={classes.backButton}
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCancel}
                >
                    Back to Merchandise
                </Button>
                <Typography variant="h4" component="h1">
                    {isCreateMode ? "Create Merchandise" : "Edit Merchandise"}
                </Typography>
            </Box>

            <Paper className={classes.formCard}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="language tabs"
                    TabIndicatorProps={{ style: { top: "0", background: "#306F9D" } }}
                >
                    <Tab
                        label="English"
                        {...a11yProps(0)}
                    />
                    <Tab
                        label="繁體中文"
                        {...a11yProps(1)}
                    />
                </Tabs>

                <TabPanel value={activeTab} index={0}>
                    <Box className={classes.innerBox} style={{ paddingTop: 32, marginBottom: 30 }}>
                        <Grid container direction="vertical" spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    variant="outlined"
                                    className={classes.formField}
                                    value={formData.titleEn}
                                    onChange={handleInputChange("titleEn")}
                                    error={!!errors.titleEn}
                                    helperText={errors.titleEn}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Subtitle"
                                    variant="outlined"
                                    className={classes.formField}
                                    value={formData.subtitleEn}
                                    onChange={handleInputChange("subtitleEn")}
                                    error={!!errors.subtitleEn}
                                    helperText={errors.subtitleEn}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" className={classes.sectionText}>
                                    Introduction
                                </Typography>
                                <CKEditor
                                    editor={Editor}
                                    config={merchandiseEditorConfig}
                                    data={formData.introductionEn || ""}
                                    onChange={handleEditorChange("introductionEn")}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <Box className={classes.innerBox} style={{ paddingTop: 32, marginBottom: 30 }}>
                        <Grid container direction="vertical" spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    variant="outlined"
                                    className={classes.formField}
                                    value={formData.titleTc}
                                    onChange={handleInputChange("titleTc")}
                                    error={!!errors.titleTc}
                                    helperText={errors.titleTc}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Subtitle"
                                    variant="outlined"
                                    className={classes.formField}
                                    value={formData.subtitleTc}
                                    onChange={handleInputChange("subtitleTc")}
                                    error={!!errors.subtitleTc}
                                    helperText={errors.subtitleTc}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" className={classes.sectionText}>
                                    Introduction
                                </Typography>
                                <CKEditor
                                    editor={Editor}
                                    config={merchandiseEditorConfig}
                                    data={formData.introductionTc || ""}
                                    onChange={handleEditorChange("introductionTc")}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </TabPanel>

                {/* Additional Fields Section */}
                <Box className={classes.innerBox} style={{ paddingTop: 32, marginBottom: 30 }}>
                    <Typography variant="h6" gutterBottom>
                        Merchandise Details
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Point Price"
                                type="number"
                                variant="outlined"
                                className={classes.formField}
                                value={formData.pointPrice}
                                onChange={handleInputChange("pointPrice")}
                                error={!!errors.pointPrice}
                                helperText={errors.pointPrice}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Order Limit"
                                type="number"
                                variant="outlined"
                                className={classes.formField}
                                value={formData.orderLimit}
                                onChange={handleInputChange("orderLimit")}
                                error={!!errors.orderLimit}
                                helperText={errors.orderLimit || "Leave empty for no limit, or enter an integer"}
                                inputProps={{ min: 0, step: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Sequence"
                                type="number"
                                variant="outlined"
                                className={classes.formField}
                                value={formData.sequence}
                                onChange={handleInputChange("sequence")}
                                error={!!errors.sequence}
                                helperText={errors.sequence}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            {/* Empty grid item to push status to new row */}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box className={classes.formField}>
                                <Typography variant="body2" className={classes.sectionText} style={{ marginBottom: "8px" }}>
                                    Status
                                </Typography>
                                <ToggleButtonGroup
                                    value={formData.status}
                                    onChange={handleStatusChange}
                                    exclusive
                                    aria-label="Status"
                                    className={classes.input}
                                >
                                    <ToggleButton
                                        value={1}
                                        aria-label="Active"
                                        style={{ width: "50%" }}
                                    >
                                        Active
                                    </ToggleButton>
                                    <ToggleButton
                                        value={0}
                                        aria-label="Inactive"
                                        style={{ width: "50%" }}
                                    >
                                        Inactive
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {errors.status && (
                                    <Typography variant="caption" color="error" style={{ marginTop: "8px", display: "block" }}>
                                        {errors.status}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Options Section */}
                <Box className={classes.innerBox} style={{ paddingTop: 32, marginBottom: 30 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                        <Typography variant="h6">
                            Options
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleAddOption}
                        >
                            Add Option
                        </Button>
                    </Box>

                    {options.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" style={{ padding: "16px" }}>
                            No options added. Click "Add Option" to create one.
                        </Typography>
                    ) : (
                        optionDisplayOrder.map((originalIdx, displayIdx) => {
                            const option = options[originalIdx];
                            return (
                            <Paper key={originalIdx} style={{ padding: "16px", marginBottom: "16px" }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                                    <Typography variant="subtitle1">
                                        Option {displayIdx + 1}
                                    </Typography>
                                    <Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveOption(displayIdx, 'up')}
                                            disabled={displayIdx === 0}
                                        >
                                            <ArrowUpwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveOption(displayIdx, 'down')}
                                            disabled={displayIdx === optionDisplayOrder.length - 1}
                                        >
                                            <ArrowDownwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteOption(originalIdx)}
                                            color="secondary"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Option (English)"
                                            variant="outlined"
                                            size="small"
                                            value={option.optionEn || ""}
                                            onChange={handleOptionChange(originalIdx, "optionEn")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Option (繁體中文)"
                                            variant="outlined"
                                            size="small"
                                            value={option.optionTc || ""}
                                            onChange={handleOptionChange(originalIdx, "optionTc")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Price"
                                            type="number"
                                            variant="outlined"
                                            size="small"
                                            value={option.price || ""}
                                            onChange={handleOptionChange(originalIdx, "price")}
                                            inputProps={{ min: 0, step: 0.01 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Stock"
                                            type="number"
                                            variant="outlined"
                                            size="small"
                                            value={option.stock || ""}
                                            onChange={handleOptionChange(originalIdx, "stock")}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ImageUploadField
                                            field={`option-${originalIdx}-image`}
                                            value={option.imagePath || ""}
                                            onUpload={(field, file) => handleOptionImageUpload(originalIdx, file)}
                                            onDelete={() => handleOptionImageDelete(originalIdx)}
                                            label="Product Image"
                                            classes={classes}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box>
                                            <Typography variant="body2" className={classes.sectionText} style={{ marginBottom: "8px" }}>
                                                Status
                                            </Typography>
                                            <ToggleButtonGroup
                                                value={option.status === true || option.status === 1 ? 1 : 0}
                                                onChange={(e, newValue) => handleOptionStatusChange(originalIdx, newValue)}
                                                exclusive
                                                aria-label="Option Status"
                                                size="small"
                                                style={{ width: "100%" }}
                                            >
                                                <ToggleButton value={1} aria-label="Active" style={{ width: "50%" }}>
                                                    Active
                                                </ToggleButton>
                                                <ToggleButton value={0} aria-label="Inactive" style={{ width: "50%" }}>
                                                    Inactive
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                            );
                        })
                    )}
                </Box>

                <Box className={classes.buttonGroup}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                    >
                        {isCreateMode ? "Create" : "Update"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </div>
    );
};

export default MerchandiseEditingPage;

